import * as React from 'react'
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView'
import { TreeItem, TreeItemProps, TreeItemSlotProps } from '@mui/x-tree-view/TreeItem'
import { FormPopoverField } from './FormPopoverField'
import { DropdownItem } from '../../components/DropdownItem'

export type DropdownTreeViewItems = {
    [key: string]: DropdownItem[]
}

type DropdownTreeViewSelectedItem = string

const CustomTreeItem = React.forwardRef(function CustomTreeItem(
    { checked, indeterminate, ...props }: TreeItemProps & { checked: boolean; indeterminate?: boolean },
    ref: React.Ref<HTMLLIElement>,
) {
    return (
        <TreeItem
            {...props}
            ref={ref}
            slotProps={
                {
                    checkbox: {
                        checked,
                        indeterminate,
                    },
                } as TreeItemSlotProps
            }
        />
    )
})

const getTextFieldValue = ({
    pluralLabel,
    items,
    selectedItems,
}: {
    pluralLabel: string
    items: DropdownTreeViewItems
    selectedItems: DropdownTreeViewSelectedItem[]
}): string => {
    const totalItemsCount = Object.values(items).flat().length

    if (totalItemsCount === selectedItems.length || selectedItems.length === 0) {
        return `Alla ${pluralLabel.toLocaleLowerCase()}`
    }

    if (selectedItems.length === 1) {
        const itemLabel = Object.values(items)
            .flat()
            .find((item) => item.id === selectedItems[0])?.label
        return itemLabel || ''
    }

    const selectedParentItemLabel = Object.keys(items).find(
        (label) =>
            items[label].filter((child) => selectedItems.includes(child.id)).length === items[label].length &&
            items[label].length === selectedItems.length,
    )

    if (selectedParentItemLabel) return selectedParentItemLabel

    return `Urval av ${pluralLabel.toLocaleLowerCase()}`
}

const DropdownTreeView = ({
    items,
    label,
    pluralLabel,
    selectedItems,
    onChange,
}: {
    items: DropdownTreeViewItems
    label: string
    pluralLabel: string
    selectedItems: DropdownTreeViewSelectedItem[]
    onChange: (items: DropdownTreeViewSelectedItem[]) => void
}) => {
    const textFieldValue = getTextFieldValue({ items, pluralLabel, selectedItems })

    const handleSelectedItemsChange = (event: React.SyntheticEvent, ids: string[]) => {
        if (!(event.target as HTMLInputElement).checked) {
            // If the user has unchecked an item, find out which is unchecked
            const uncheckedItem = selectedItems.find((i) => !ids.includes(i))

            if (!uncheckedItem) return

            const filteredItems = selectedItems.filter((i) => i !== uncheckedItem)

            onChange(filteredItems)
            return
        }

        // Map parent ids to its child items
        const selectedChildItems = ids.map((id) => items[id]?.map((item) => item.id) || id).flat()

        onChange(selectedChildItems)
    }

    const getCheckboxProps = (id: string) => {
        const hasChildren = !!items[id]

        if (hasChildren) {
            const checkedChildrenCount = items[id].filter((child) => selectedItems.includes(child.id)).length
            const isChecked = checkedChildrenCount === items[id].length
            const isIndeterminate = !isChecked && checkedChildrenCount > 0

            return {
                checked: !!isChecked,
                indeterminate: isIndeterminate,
            }
        }

        return {
            checked: !!selectedItems.find((_id) => _id === id),
            indeterminate: false,
        }
    }

    const handleItemClick = (event: React.MouseEvent, id: string) => {
        if ((event.target as HTMLElement).tagName !== 'INPUT') return

        const hasChildren = !!items[id]
        if (!hasChildren) return

        const childItems = items[id]
        const checkedChildrenCount = childItems.filter((child) => selectedItems.includes(child.id)).length
        const isPreviouslyChecked = checkedChildrenCount === childItems.length

        if (isPreviouslyChecked) {
            // Uncheck all child items
            onChange(selectedItems.filter((i) => !childItems.find((_i) => _i.id === i)))
        }
    }

    const hasOneChild = (items: DropdownItem[]) => items.length === 1

    return (
        <FormPopoverField label={label} textFieldValue={textFieldValue}>
            <SimpleTreeView
                checkboxSelection
                multiSelect
                onSelectedItemsChange={handleSelectedItemsChange}
                onItemClick={handleItemClick}
                selectedItems={selectedItems ?? []}
                sx={{
                    '.Mui-selected, .Mui-focused': {
                        backgroundColor: 'unset',
                    },
                }}
            >
                {Object.keys(items)
                    .sort((a, b) => a.localeCompare(b, 'sv'))
                    .map((subHeader) => (
                        <CustomTreeItem
                            key={subHeader}
                            itemId={hasOneChild(items[subHeader]) ? items[subHeader][0].id : subHeader}
                            label={subHeader}
                            {...getCheckboxProps(hasOneChild(items[subHeader]) ? items[subHeader][0].id : subHeader)}
                        >
                            {!hasOneChild(items[subHeader]) &&
                                items[subHeader]
                                    .sort((a, b) => a.label.localeCompare(b.label, 'sv'))
                                    .map((subHeader2) => (
                                        <CustomTreeItem
                                            key={subHeader2.id}
                                            itemId={`${subHeader2.id}`}
                                            label={subHeader2.label}
                                            checked={selectedItems.includes(subHeader2.id)}
                                        />
                                    ))}
                        </CustomTreeItem>
                    ))}
            </SimpleTreeView>
        </FormPopoverField>
    )
}

export default DropdownTreeView
